import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, SLUG_TO_NAME } from "@/lib/api";
import Seo from "@/components/Seo";
import ArticleCard from "@/components/ArticleCard";

export default function Category() {
  const { slug } = useParams();
  const name = SLUG_TO_NAME[slug] || slug;
  const [articles, setArticles] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [format, setFormat] = useState("");
  const [series, setSeries] = useState("");

  useEffect(() => {
    setLoading(true);
    setQuery(""); setFormat(""); setSeries("");
    (async () => {
      const [a, c] = await Promise.all([
        api.get(`/articles?category=${encodeURIComponent(name)}`),
        api.get("/categories"),
      ]);
      setArticles(a.data || []);
      setMeta((c.data || []).find((x) => x.slug === slug) || null);
      setLoading(false);
    })();
  }, [slug, name]);

  const seriesOptions = useMemo(
    () => [...new Set(articles.map((a) => a.series).filter((s) => s && s !== "None"))],
    [articles]
  );

  const q = query.trim().toLowerCase();
  const filtered = articles.filter((a) => {
    if (q && !(`${a.title || ""} ${a.excerpt || ""}`.toLowerCase().includes(q))) return false;
    if (format && (a.format || "Read") !== format) return false;
    if (series && a.series !== series) return false;
    return true;
  });

  const Chip = ({ on, onClick, children, testId }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`font-head uppercase tracking-wide text-xs px-4 py-2 border-2 transition-colors ${
        on ? "bg-[#2D2D2D] text-[#F3F1EC] border-[#2D2D2D]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-testid="category-page">
      <Seo title={name} description={meta?.description || `${name} — bench-tested reads from Voluntold.`} path={`/category/${slug}`} />
      <div className="border-b-2 border-[#1E1E1E] pb-6 mb-8">
        <span className="kicker">Category</span>
        <h1 className="mt-2 font-head font-semibold uppercase text-4xl lg:text-6xl text-[#1E1E1E]">{name}</h1>
        {meta && <p className="mt-3 text-[#6E6E6E] text-lg max-w-2xl">{meta.description}</p>}
        <p className="mt-2 text-sm text-[#6E6E6E]">{filtered.length} of {articles.length} articles</p>
      </div>

      {/* in-page search + filter chips */}
      {!loading && articles.length > 0 && (
        <div className="mb-10 space-y-4" data-testid="category-filters">
          <div>
            <label htmlFor="category-search" className="sr-only">Search this category</label>
            <input
              id="category-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search these articles…"
              data-testid="category-search"
              className="w-full max-w-md bg-white border-2 border-[#1E1E1E] px-4 py-3 text-sm text-[#1E1E1E] placeholder:text-[#9AA1A6] focus:outline-none focus:border-[#DB9130] transition-colors"
            />
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="kicker mr-1">Format</span>
            <Chip on={!format} onClick={() => setFormat("")} testId="chip-format-all">All</Chip>
            <Chip on={format === "Read"} onClick={() => setFormat("Read")} testId="chip-format-read">Read</Chip>
            <Chip on={format === "Watch"} onClick={() => setFormat("Watch")} testId="chip-format-watch">Watch</Chip>
          </div>
          {seriesOptions.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="kicker mr-1">Series</span>
              <Chip on={!series} onClick={() => setSeries("")} testId="chip-series-all">All</Chip>
              {seriesOptions.map((s) => (
                <Chip key={s} on={series === s} onClick={() => setSeries(s)} testId={`chip-series-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s}</Chip>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-[#6E6E6E]">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-[#6E6E6E]" data-testid="category-empty">
          {articles.length === 0 ? "No articles here yet." : "No articles match those filters."}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((a, i) => (
            <ArticleCard key={a.slug} article={a} testId={`category-article-${i}`} />
          ))}
        </div>
      )}

      <div className="mt-14 border-t border-[#D3CFC0] pt-6">
        <Link to="/" className="text-[#2D2D2D] link-line text-sm">← Back to all articles</Link>
      </div>
    </div>
  );
}
